import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Flame, ArrowUpRight } from "lucide-react";
import { api, formatINR, BACKEND_URL } from "@/lib/api";
import { bestTierDiscount, sortedTiers } from "@/lib/pricing";
import { useAuth } from "@/context/AuthContext";

export default function HotOffers() {
  const [products, setProducts] = useState([]);
  const { salon } = useAuth();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, [salon?.status]);

  const offers = useMemo(() => {
    return products
      .map((p) => ({ ...p, _discount: bestTierDiscount(p) }))
      .filter((p) => p._discount > 0)
      .sort((a, b) => b._discount - a._discount)
      .slice(0, 4);
  }, [products]);

  // Hot bulk offers are wholesale-only. Public users never see this section.
  if (salon?.status !== "approved") return null;
  if (!offers.length) return null;

  return (
    <section className="py-20 md:py-24 bg-black text-white relative overflow-hidden" data-testid="hot-offers-section">
      <div className="absolute -top-10 -right-10 w-60 h-60 bg-[#C9A961]/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-10 -left-10 w-72 h-72 bg-[#C9A961]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-12 gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Flame size={16} className="text-[#C9A961]" strokeWidth={1.6} />
              <span className="overline text-[#C9A961]">Hot Bulk Offers · Live Now</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-6xl tracking-tighter font-light leading-[1.02]">
              Stock up.<br />
              <em className="text-[#C9A961] not-italic">Save up to {Math.max(...offers.map((o) => o._discount))}%.</em>
            </h2>
          </div>
          <Link to="/shop" className="overline hover:text-[#C9A961] transition-colors flex items-center gap-2" data-testid="hot-offers-view-all">
            View All Offers <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {offers.map((p, i) => {
            const tiers = sortedTiers(p.tiers);
            const lowestTier = tiers[tiers.length - 1];
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
              >
                <Link
                  to={`/product/${p.id}`}
                  className="group block bg-[#0f0f0f] border border-white/5 hover:border-[#C9A961]/40 transition-colors"
                  data-testid={`hot-offer-${p.id}`}
                >
                  <div className="aspect-[4/5] overflow-hidden bg-[#1a1a1a] relative">
                    <img
                      src={p.image.startsWith("http") || p.image.startsWith("data:") ? p.image : `${BACKEND_URL}${p.image}`}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 bg-[#C9A961] text-black text-[10px] tracking-[0.3em] uppercase px-2.5 py-1 font-medium">
                      −{p._discount}%
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="overline text-white/50 mb-1">{p.category}</div>
                    <div className="font-serif-display text-xl leading-tight line-clamp-2 group-hover:text-[#C9A961] transition-colors">
                      {p.name}
                    </div>
                    <div className="mt-3 flex items-baseline gap-2">
                      <span className="font-serif-display text-2xl">{formatINR(lowestTier.price)}</span>
                      <span className="text-xs text-white/50 line-through">{formatINR(p.price)}</span>
                    </div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#C9A961] mt-1">
                      {lowestTier.label || `${lowestTier.min_qty} PCS`} · per unit
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
